import { MODULE_REQUEST_STATUSES, type ModuleRequest } from '@sentinel/shared';
import { Button, EmptyState, Panel, StateBadge, Table, type Column } from '@sentinel/ui';
import { formatDateTime, QueryState, toast, useErrorMessage } from '@sentinel/web-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { consoleEndpoints } from '@/api/endpoints';
import { keys } from '@/api/keys';
import { useConsoleCan } from '@/auth/use-console-can';

const K = 'console.requests';
const FILTERS = [...MODULE_REQUEST_STATUSES, 'all'] as const;
const TONE = { pending: 'info', granted: 'ok', declined: 'off' } as const;

/** Modules the organizations asked for (Settings → Modules): granting makes the module
 *  available and switched on, declining closes the request. */
export function ModuleRequestsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const canManage = useConsoleCan('console.entitlements:manage');
  const [status, setStatus] = useState<(typeof FILTERS)[number]>('pending');
  const list = useQuery({
    queryKey: keys.moduleRequests(status),
    queryFn: () => consoleEndpoints.moduleRequests(status),
  });
  const handle = useMutation({
    mutationFn: (input: { request: ModuleRequest; grant: boolean }) =>
      input.grant
        ? consoleEndpoints.grantRequest(input.request.id, { entitlement: 'included' })
        : consoleEndpoints.declineRequest(input.request.id),
    onSuccess: (_result, input) => {
      toast('ok', t(`${K}.${input.grant ? 'granted' : 'declined'}`));
      void queryClient.invalidateQueries({ queryKey: ['console', 'module-requests'] });
    },
    onError: (error) => toast('att', errorMessage(error)),
  });

  const columns: Column<ModuleRequest>[] = [
    {
      key: 'org',
      header: t(`${K}.columns.organization`),
      render: (r) => <b>{r.organizationName}</b>,
    },
    {
      key: 'module',
      header: t(`${K}.columns.module`),
      render: (r) => t(`modules.names.${r.moduleKey}`),
    },
    { key: 'note', header: t(`${K}.columns.note`), render: (r) => r.note },
    {
      key: 'when',
      header: t(`${K}.columns.when`),
      mono: true,
      render: (r) => formatDateTime(r.createdAt),
    },
    {
      key: 'status',
      header: t(`${K}.columns.status`),
      render: (r) => <StateBadge tone={TONE[r.status]} label={t(`${K}.statuses.${r.status}`)} />,
    },
    {
      key: 'act',
      header: '',
      render: (r) =>
        canManage && r.status === 'pending' ? (
          <span style={{ display: 'flex', gap: 6 }}>
            <Button
              size="sm"
              disabled={handle.isPending}
              onClick={() => handle.mutate({ request: r, grant: true })}
            >
              {t(`${K}.grant`)}
            </Button>
            <Button
              size="sm"
              variant="danger"
              disabled={handle.isPending}
              onClick={() => handle.mutate({ request: r, grant: false })}
            >
              {t(`${K}.decline`)}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <Panel flush title={t(`${K}.title`)}>
      <div className="toolbar">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            className={['chip', status === f ? 'on' : ''].join(' ').trim()}
            onClick={() => setStatus(f)}
          >
            {t(`${K}.filters.${f}`)}
          </button>
        ))}
      </div>
      <QueryState query={list}>
        {(data) =>
          data.items.length === 0 ? (
            <EmptyState title={t(`${K}.empty`)} description={t(`${K}.emptyHint`)} />
          ) : (
            <Table
              caption={t(`${K}.title`)}
              columns={columns}
              rows={data.items}
              rowKey={(r) => r.id}
            />
          )
        }
      </QueryState>
    </Panel>
  );
}
